import React from 'react';
import { FormikState } from 'formik';
import { SessionThresholds } from 'screens/GroupsAndPolicies/types';
import { Grid, Header, Segment, Divider, Label } from 'semantic';
import { useTranslation } from 'react-i18next';
import ThemedSegment from 'components/semantic/Segment';
import { currencyToSymbol } from 'utils/formatting';

type Props = {
  values?: FormikState<SessionThresholds>['values'];
  currency?: string;
};

export function SessionThresholdsPreview({ values, currency }: Props) {
  const { t } = useTranslation();

  return (
    <Segment>
      <Grid columns={2}>
        <Grid.Row stretched>
          <Grid.Column>
            <ThemedSegment offbackground>
              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.gracePeriodTitle',
                  'Grace period'
                )}
              />
              <Divider />
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p>
                      {t(
                        'chargingPolicyForm.minSessionTime',
                        'Min session time'
                      )}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <DisplayValue
                      hasValue={!!values?.minTimeMinutes}
                      valueString={`${values?.minTimeMinutes} ${t(
                        'chargingPolicyForm.minutesShorthand',
                        'mins'
                      )}`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p>
                      {t(
                        'chargingPolicyForm.minEnergyConsumed',
                        'Min energy consumed'
                      )}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <DisplayValue
                      hasValue={!!values?.minKwhConsumed}
                      valueString={`${values?.minKwhConsumed} ${t('chargingPolicyForm.kwh', 'kWh')}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </ThemedSegment>
          </Grid.Column>
          <Grid.Column stretched>
            <ThemedSegment offbackground>
              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.sessionLimitsTitle',
                  'Session limits'
                )}
              />
              <Divider />
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p>
                      {t(
                        'chargingPolicyForm.maxSessionTime',
                        'Max session time'
                      )}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <DisplayValue
                      hasValue={!!values?.maxTimeMinutes}
                      valueString={`${values?.maxTimeMinutes} ${t(
                        'chargingPolicyForm.minutesShorthand',
                        'mins'
                      )}`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p>
                      {t(
                        'chargingPolicyForm.maxEnergyConsumed',
                        'Max energy consumed'
                      )}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <DisplayValue
                      hasValue={!!values?.maxKwhConsumed}
                      valueString={`${values?.maxKwhConsumed} ${t('chargingPolicyForm.kwh', 'kWh')}`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p>
                      {t(
                        'chargingPolicyForm.maxSessionCost',
                        'Max session cost'
                      )}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <DisplayValue
                      hasValue={!!values?.maxCost}
                      // TODO: is this a good / valid default?
                      valueString={`${currencyToSymbol(currency || 'EUR')}${values?.maxCost}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </ThemedSegment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
}

function DisplayValue({
  valueString,
  hasValue,
}: {
  valueString?: string;
  hasValue?: boolean;
}) {
  if (hasValue) {
    // TODO: make white? (and support for dark mode)
    return <Label color="green">{valueString}</Label>;
  }

  return <p>-</p>;
}
